import React, { createContext, useContext, useEffect, useState } from "react";

import { StatusCode } from "api/protocols";
import { UserActive } from "api/rpc";
import { IUser, IUserActive } from "redux/reducers/models/user";

import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetLoyaltyProgram } from "api/rpc/2024-04/customer/loyaltyProgram/loyaltyProgram";

export interface IGuestPortalContextProps {
  /**
   * - Undefined  ??  Fresh reload
   * - Null  ??  No active user
   */
  guest?: IUser | IUserActive;
  loading?: boolean;
  showLoyaltyTab?: boolean;
}

const defaultState: PortalContextType = {
  portalState: {
    guest: undefined,
    loading: false,
    showLoyaltyTab: false,
  },
  updatePortalState: null,
};

type PortalContextType = {
  portalState: IGuestPortalContextProps;
  updatePortalState: (curr: Partial<IGuestPortalContextProps>) => void;
};


const GuestPortalContext = createContext<PortalContextType>(defaultState);
export const useGuestPortalContext = () => useContext(GuestPortalContext);

/**
 *  Determines if active user.  Syncs found active user to state.
 *  - portalState.guest will not be null to children.
 * @returns portalState
 * @returns updatePortalState (curr: portalState) => void
 *
 */
const GuestPortalProvider = (props: IGuestPortalContextProps & { children: any }) => {

  const [portalState, setPortalState] = useState<IGuestPortalContextProps>({
    guest: undefined,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;

    if (portalState.guest === undefined) {
      void checkActiveUser(mounted);
    }

    return () => {
      mounted = false;
    };
  }, [portalState.guest]);

  async function checkActiveUser(stillMounted: boolean) {
    if (!portalState.loading) {
      updatePortalState({ loading: true });
    }
    const activeUserRes = await UserActive(false);
    let showLoyaltyTab = false;
    if (activeUserRes.status === StatusCode.OK && stillMounted) {

     const [prefRes, clientRes] = await Promise.all([
           GetLoyaltyProgram(null, false),
           GetClient({ collect: true }, false),
         ]);

         if (prefRes.status === StatusCode.OK && clientRes.status === StatusCode.OK) {
          showLoyaltyTab = clientRes?.data?.some(client => prefRes?.data?.some(loyalty => loyalty?.client_id === client?.id));
         }
        
      // Sync active user to context
      updatePortalState({ guest: activeUserRes?.data, loading: false, showLoyaltyTab });
      console.log("user re-initialized, ", activeUserRes?.data); //debug
    } else {
      // No active user
      updatePortalState({ guest: null, loading: false, showLoyaltyTab: showLoyaltyTab });
    }
  }

  function updatePortalState(updatedState: Partial<IGuestPortalContextProps>) {
    setPortalState(currentState => {
      return { ...currentState, ...updatedState };
    });
  }

  return (
    <GuestPortalContext.Provider value={{ portalState, updatePortalState }}>
      {props.children}
    </GuestPortalContext.Provider>
  );
};

export default GuestPortalProvider;
